import {Inspector,TechnicalDisclosure} from "./os/Inspector";
import {useOsText} from "./os/messages";
import {IdentityAccessWorkspace} from "../features/security/IdentityAccessWorkspace";
import {RegistrationsView} from "../features/security/RegistrationsView";
import {RecoveryView,SessionsView} from "../features/security/ResourceViews";
import { useEffect, useState } from "react";
import type { SecuritySection } from "../lib/types";
import { useAppI18n } from "../lib/appI18n";
import { deviceAuthorityApi } from "../lib/api";
import { runPrfCeremony, runRegistrationCeremony } from "../lib/webauthn";

type ActionId =
  | "add-user"
  | "add-passkey"
  | "grant-access"
  | "temporary-access"
  | "recovery"
  | "sessions";

type Lang = "en" | "tr" | "de";
const INTENT_KEY="knsaix.security.taskIntent.v1";

type ActionDef = {
  id: ActionId;
  section: SecuritySection;
  title: Record<Lang,string>;
  detail: Record<Lang,string>;
  steps: Record<Lang,string[]>;
};

const ACTIONS: ActionDef[] = [
  {
    id:"add-user", section:"users",
    title:{en:"Add user",tr:"Kullanıcı ekle",de:"Benutzer hinzufügen"},
    detail:{
      en:"Create or onboard a person and complete their access setup without hunting through Security.",
      tr:"Bir kişiyi oluşturun veya sisteme alın; erişim kurulumunu Security içinde ekran aramadan tamamlayın.",
      de:"Erstellen oder übernehmen Sie eine Person und schließen Sie deren Zugriffseinrichtung ohne Seitensuche ab."
    },
    steps:{
      en:["Choose the person","Confirm identity details","Assign initial access","Review and finish"],
      tr:["Kişiyi seç","Kimlik bilgilerini doğrula","İlk erişimi ata","İncele ve tamamla"],
      de:["Person auswählen","Identitätsdaten bestätigen","Ersten Zugriff zuweisen","Prüfen und abschließen"]
    }
  },
  {
    id:"add-passkey", section:"devices",
    title:{en:"Add passkey",tr:"Passkey ekle",de:"Passkey hinzufügen"},
    detail:{
      en:"Register a passkey for sign-in, vault unlock or recovery and keep the user context visible.",
      tr:"Giriş, kasa açma veya kurtarma amacıyla passkey kaydedin; kullanıcı bağlamını görünür tutun.",
      de:"Registrieren Sie einen Passkey für Anmeldung, Tresor-Entsperrung oder Wiederherstellung bei sichtbarem Benutzerkontext."
    },
    steps:{
      en:["Choose user","Choose passkey purpose","Run registration","Verify result"],
      tr:["Kullanıcıyı seç","Passkey amacını seç","Kaydı başlat","Sonucu doğrula"],
      de:["Benutzer auswählen","Passkey-Zweck wählen","Registrierung starten","Ergebnis prüfen"]
    }
  },
  {
    id:"grant-access", section:"access",
    title:{en:"Grant access",tr:"Yetki ver",de:"Zugriff gewähren"},
    detail:{
      en:"Assign a role, package or individual capability from one guided flow.",
      tr:"Rol, erişim paketi veya tekil yetkiyi tek yönlendirmeli akıştan atayın.",
      de:"Weisen Sie Rolle, Zugriffspaket oder Einzelberechtigung in einem geführten Ablauf zu."
    },
    steps:{
      en:["Choose user","Choose access type","Set scope and reason","Fresh passkey and apply"],
      tr:["Kullanıcıyı seç","Yetki türünü seç","Kapsam ve nedeni belirle","Fresh passkey ile uygula"],
      de:["Benutzer auswählen","Zugriffsart wählen","Umfang und Grund festlegen","Mit frischem Passkey anwenden"]
    }
  },
  {
    id:"temporary-access", section:"requests",
    title:{en:"Temporary access",tr:"Geçici erişim ver",de:"Temporären Zugriff gewähren"},
    detail:{
      en:"Create time-bounded privileged access with explicit reason and expiry.",
      tr:"Açık gerekçe ve bitiş süresiyle zaman sınırlı ayrıcalıklı erişim oluşturun.",
      de:"Erstellen Sie zeitlich begrenzten privilegierten Zugriff mit Begründung und Ablaufzeit."
    },
    steps:{
      en:["Choose user","Choose capability","Set expiry","Approve and verify"],
      tr:["Kullanıcıyı seç","Yetkiyi seç","Bitiş süresini belirle","Onayla ve doğrula"],
      de:["Benutzer auswählen","Berechtigung wählen","Ablauf festlegen","Bestätigen und prüfen"]
    }
  },
  {
    id:"recovery", section:"recovery",
    title:{en:"Recovery setup",tr:"Kurtarma ayarla",de:"Wiederherstellung einrichten"},
    detail:{
      en:"Inspect recovery health and add or repair recovery methods.",
      tr:"Kurtarma sağlığını inceleyin; eksik yöntemleri ekleyin veya onarın.",
      de:"Prüfen Sie den Wiederherstellungsstatus und ergänzen oder reparieren Sie Methoden."
    },
    steps:{
      en:["Choose user","Inspect protection gaps","Add or repair method","Verify coverage"],
      tr:["Kullanıcıyı seç","Koruma açıklarını incele","Yöntem ekle veya onar","Kapsamı doğrula"],
      de:["Benutzer auswählen","Schutzlücken prüfen","Methode ergänzen oder reparieren","Abdeckung prüfen"]
    }
  },
  {
    id:"sessions", section:"sessions",
    title:{en:"Manage sessions",tr:"Oturumları yönet",de:"Sitzungen verwalten"},
    detail:{
      en:"Review active sessions and terminate suspicious or no-longer-needed access.",
      tr:"Aktif oturumları inceleyin; şüpheli veya artık gerekmeyen erişimleri sonlandırın.",
      de:"Prüfen Sie aktive Sitzungen und beenden Sie verdächtige oder nicht mehr benötigte Zugriffe."
    },
    steps:{
      en:["Choose user","Review active sessions","Select session","Terminate and verify"],
      tr:["Kullanıcıyı seç","Aktif oturumları incele","Oturumu seç","Sonlandır ve doğrula"],
      de:["Benutzer auswählen","Aktive Sitzungen prüfen","Sitzung auswählen","Beenden und prüfen"]
    }
  }
];

const COPY = {
  en:{
    action:"Action",help:"Help",close:"Close",quick:"Action Engine",control:"Security Control Center",
    intro:"Start common Security work here. The flow keeps the purpose visible and guides you to the required control.",
    what:"What can I do here?",why:"Why this helps",
    whyText:"Security is organized around your task first. Expert pages remain available for inspection and audit.",
    continue:"Continue",back:"Back",step:"Step",of:"of",
    current:"Current step",technical:"Technical details",
    techText:"Canonical identifiers and security policy remain unchanged. This layer only guides the operator through existing governed controls.",
    hint:"You stay in the same Security workspace while the relevant control opens."
  },
  tr:{
    action:"İşlem",help:"Yardım",close:"Kapat",quick:"İşlem Merkezi",control:"Güvenlik Kontrol Merkezi",
    intro:"Sık kullanılan Security işlemlerini buradan başlatın. Akış amacı görünür tutar ve gerekli kontrole yönlendirir.",
    what:"Bu ekranda ne yapabilirim?",why:"Bu neden yardımcı olur?",
    whyText:"Security önce yapmak istediğiniz işe göre düzenlenir. Uzman ekranları inceleme ve denetim için erişilebilir kalır.",
    continue:"Devam et",back:"Geri",step:"Adım",of:"/",
    current:"Şu anki adım",technical:"Teknik ayrıntılar",
    techText:"Canonical kimlikler ve güvenlik politikası değişmez. Bu katman yalnız mevcut kontrollü işlemler içinde operatörü yönlendirir.",
    hint:"İlgili kontrol açılırken aynı Security çalışma alanında kalırsınız."
  },
  de:{
    action:"Aktion",help:"Hilfe",close:"Schließen",quick:"Aktionszentrale",control:"Sicherheits-Kontrollzentrum",
    intro:"Starten Sie häufige Security-Aufgaben hier. Der Ablauf hält das Ziel sichtbar und führt zur benötigten Steuerung.",
    what:"Was kann ich hier tun?",why:"Warum hilft das?",
    whyText:"Security wird zuerst nach Ihrer Aufgabe organisiert. Expertenansichten bleiben für Prüfung und Audit verfügbar.",
    continue:"Weiter",back:"Zurück",step:"Schritt",of:"von",
    current:"Aktueller Schritt",technical:"Technische Details",
    techText:"Kanonische Kennungen und Sicherheitsrichtlinien bleiben unverändert. Diese Ebene führt nur durch bestehende kontrollierte Funktionen.",
    hint:"Sie bleiben im selben Security-Arbeitsbereich, während die passende Steuerung geöffnet wird."
  }
} as const;


// KNSAIX_SECURITY_UX_V3_PASSKEY_LIVE_P23
const PASSKEY_COPY={
  en:{
    title:"Passkey for this account",
    desc:"Add a new passkey to the currently signed-in Security identity. Another person must complete their own passkey registration on their device.",
    label:"Passkey name",
    labelHint:"For example: MacBook Touch ID or YubiKey 5",
    trust:"Recovery group",
    trustHint:"Keeps independent recovery devices separated.",
    add:"Create passkey",
    none:"No passkeys returned for this account.",
    current:"Current passkeys",
    revoke:"Revoke",
    confirm:"Confirm revoke",
    cancel:"Cancel",
    busy:"Waiting for passkey ceremony…",
    currentProof:"Approve with your current unlock-capable passkey.",
    newProof:"Now create the new passkey on this device.",
    success:"Passkey added and verified.",
    revoked:"Passkey revoked.",
    failed:"The passkey operation could not be completed.",
    authOnly:"Sign-in only (no vault unlock)",
    advanced:"Advanced",
    mode:"Mode",
    unlock:"Vault unlock",
    auth:"Sign-in only",
    why:"Why can't I create one for another user?",
    whyText:"A passkey proves possession of that person's authenticator. KNSAIX therefore binds enrollment to the signed-in subject; another user must complete the WebAuthn ceremony themselves."
  },
  tr:{
    title:"Bu hesap için passkey",
    desc:"Oturum açmış Security kimliğine yeni passkey ekleyin. Başka bir kişi kendi passkey kaydını kendi cihazında tamamlamalıdır.",
    label:"Passkey adı",
    labelHint:"Örnek: MacBook Touch ID veya YubiKey 5",
    trust:"Kurtarma grubu",
    trustHint:"Bağımsız kurtarma cihazlarını birbirinden ayırır.",
    add:"Passkey oluştur",
    none:"Bu hesap için passkey bulunamadı.",
    current:"Mevcut passkey'ler",
    revoke:"İptal et",
    confirm:"İptali onayla",
    cancel:"Vazgeç",
    busy:"Passkey doğrulaması bekleniyor…",
    currentProof:"Mevcut kasa-açabilen passkey'inizle onaylayın.",
    newProof:"Şimdi bu cihazda yeni passkey'i oluşturun.",
    success:"Passkey eklendi ve doğrulandı.",
    revoked:"Passkey iptal edildi.",
    failed:"Passkey işlemi tamamlanamadı.",
    authOnly:"Yalnız giriş (kasa açamaz)",
    advanced:"Gelişmiş",
    mode:"Mod",
    unlock:"Kasa açma",
    auth:"Yalnız giriş",
    why:"Neden başka kullanıcı için ben oluşturamıyorum?",
    whyText:"Passkey o kişinin doğrulayıcısına sahip olduğunu kanıtlar. KNSAIX bu nedenle kaydı oturumdaki kimliğe bağlar; diğer kullanıcı WebAuthn işlemini kendisi tamamlamalıdır."
  },
  de:{
    title:"Passkey für dieses Konto",
    desc:"Fügen Sie der aktuell angemeldeten Security-Identität einen neuen Passkey hinzu. Eine andere Person muss ihre Passkey-Registrierung auf dem eigenen Gerät abschließen.",
    label:"Passkey-Name",
    labelHint:"Zum Beispiel: MacBook Touch ID oder YubiKey 5",
    trust:"Wiederherstellungsgruppe",
    trustHint:"Trennt unabhängige Wiederherstellungsgeräte voneinander.",
    add:"Passkey erstellen",
    none:"Für dieses Konto wurden keine Passkeys zurückgegeben.",
    current:"Vorhandene Passkeys",
    revoke:"Widerrufen",
    confirm:"Widerruf bestätigen",
    cancel:"Abbrechen",
    busy:"Warte auf Passkey-Bestätigung…",
    currentProof:"Mit dem aktuellen entsperrfähigen Passkey bestätigen.",
    newProof:"Jetzt den neuen Passkey auf diesem Gerät erstellen.",
    success:"Passkey hinzugefügt und bestätigt.",
    revoked:"Passkey widerrufen.",
    failed:"Der Passkey-Vorgang konnte nicht abgeschlossen werden.",
    authOnly:"Nur Anmeldung (keine Tresorentsperrung)",
    advanced:"Erweitert",
    mode:"Modus",
    unlock:"Tresorentsperrung",
    auth:"Nur Anmeldung",
    why:"Warum kann ich keinen Passkey für einen anderen Benutzer erstellen?",
    whyText:"Ein Passkey beweist den Besitz des Authenticator-Geräts dieser Person. KNSAIX bindet die Registrierung deshalb an die angemeldete Identität; andere Benutzer müssen die WebAuthn-Zeremonie selbst abschließen."
  }
} as const;

function LivePasskeyTask({lang,onBusyChange}:{lang:Lang;onBusyChange:(busy:boolean)=>void}){
  const p=PASSKEY_COPY[lang];
  const [credentials,setCredentials]=useState<Array<Record<string,unknown>>>([]);
  const [label,setLabel]=useState("");
  const [trustGroup,setTrustGroup]=useState("unclassified");
  const [authOnly,setAuthOnly]=useState(false);
  const [busy,setBusy]=useState(false);
  useEffect(()=>{onBusyChange(busy);return()=>onBusyChange(false);},[busy,onBusyChange]);
  const [phase,setPhase]=useState<"idle"|"current"|"new">("idle");
  const [notice,setNotice]=useState<{tone:"ok"|"error";text:string}|null>(null);
  const [pendingRevoke,setPendingRevoke]=useState<string|null>(null);

  const load=async()=>{
    try{
      const state=await deviceAuthorityApi.state();
      setCredentials(Array.isArray(state.credentials)?state.credentials:[]);
    }catch{
      setCredentials([]);
    }
  };

  useEffect(()=>{void load()},[]);

  const enroll=async()=>{
    if(!label.trim()||busy)return;
    setBusy(true);
    setNotice(null);
    try{
      const options=await deviceAuthorityApi.enrollOptions(label.trim());
      setPhase("current");
      const current=await runPrfCeremony(options.ceremony_id,options.currentCredential);
      setPhase("new");
      const created=await runRegistrationCeremony({
        ceremony_id:options.ceremony_id,
        publicKey:options.newCredential,
      });
      await deviceAuthorityApi.enrollVerify({
        ceremony_id:options.ceremony_id,
        current_credential:current.credential,
        current_prf_result:current.prf_result,
        new_credential:created.new_credential,
        new_prf_result:created.new_prf_result,
        allow_auth_only:authOnly,
        label:label.trim(),
        trust_group:trustGroup.trim()||"unclassified",
      });
      setLabel("");
      setPhase("idle");
      setNotice({tone:"ok",text:p.success});
      await load();
    }catch{
      setPhase("idle");
      setNotice({tone:"error",text:p.failed});
    }finally{
      setBusy(false);
    }
  };

  const revoke=async(id:string)=>{
    if(busy)return;
    setBusy(true);
    setNotice(null);
    try{
      const options=await deviceAuthorityApi.stepUpOptions();
      const proof=await runPrfCeremony(options.ceremony_id,options.publicKey);
      await deviceAuthorityApi.revokeCredential(id,proof);
      setPendingRevoke(null);
      setNotice({tone:"ok",text:p.revoked});
      await load();
    }catch{
      setNotice({tone:"error",text:p.failed});
    }finally{
      setBusy(false);
    }
  };

  return <div className="sux3-passkey-live">
    <div className="sux3-guide-card">
      <strong>{p.title}</strong>
      <p>{p.desc}</p>
    </div>

    {notice&&<div className={`sux3-live-notice ${notice.tone}`}>{notice.text}</div>}

    {busy&&<div className="sux3-live-progress">
      <span className="sux3-live-pulse" aria-hidden="true"/>
      <div>
        <strong>{p.busy}</strong>
        <small>{phase==="current"?p.currentProof:phase==="new"?p.newProof:p.busy}</small>
      </div>
    </div>}

    <div className="sux3-passkey-form">
      <label>
        <span>{p.label}</span>
        <input value={label} onChange={e=>setLabel(e.target.value)} placeholder={p.labelHint} maxLength={255}/>
      </label>
      <label>
        <span>{p.trust}</span>
        <input value={trustGroup} onChange={e=>setTrustGroup(e.target.value)} maxLength={128}/>
        <small>{p.trustHint}</small>
      </label>
      <details>
        <summary>{p.advanced}</summary>
        <label className="sux3-check">
          <input type="checkbox" checked={authOnly} onChange={e=>setAuthOnly(e.target.checked)}/>
          <span>{p.authOnly}</span>
        </label>
      </details>
      <button type="button" className="button primary" disabled={busy||!label.trim()} onClick={()=>void enroll()}>{p.add}</button>
    </div>

    <div className="sux3-passkey-list">
      <strong>{p.current}</strong>
      {!credentials.length?<p className="sux3-intro">{p.none}</p>:credentials.map((x,i)=>{
        const id=String(x.credential_uuid??x.id??"");
        const itemLabel=String(x.label??`Passkey ${i+1}`);
        const unlock=Boolean(x.unlock_capable);
        const revoked=Boolean(x.revoked_at)||String(x.status??"").toUpperCase()==="REVOKED";
        return <div className="sux3-passkey-row" key={id||i}>
          <div>
            <strong>{itemLabel}</strong>
            <small>{p.mode}: {unlock?p.unlock:p.auth}</small>
          </div>
          {!revoked&&id&&<div className="sux3-row-actions">
            {pendingRevoke===id?<>
              <button type="button" className="button secondary" disabled={busy} onClick={()=>setPendingRevoke(null)}>{p.cancel}</button>
              <button type="button" className="button danger" disabled={busy} onClick={()=>void revoke(id)}>{p.confirm}</button>
            </>:<button type="button" className="button secondary" disabled={busy} onClick={()=>setPendingRevoke(id)}>{p.revoke}</button>}
          </div>}
        </div>;
      })}
    </div>

    <details className="sux3-tech">
      <summary>{p.why}</summary>
      <p>{p.whyText}</p>
    </details>
  </div>;
}

export function SecurityTaskCenter({section}:{section:SecuritySection;onSection:(section:SecuritySection)=>void}){
 const {locale}=useAppI18n(),text=useOsText();
 const lang:Lang=locale,c=COPY[lang];
 const [open,setOpen]=useState<"actions"|"help"|null>(null);
 const [actionId,setActionId]=useState<ActionId|null>(null);
 const [taskBusy,setTaskBusy]=useState(false);
 const action=ACTIONS.find(x=>x.id===actionId);
 const relevant=[...ACTIONS].sort((a,b)=>Number(b.section===section)-Number(a.section===section));
 const select=(id:ActionId)=>{setActionId(id);};
 return <div className="os-action-anchor" data-security-section={section}>
  <button type="button" className="os-action-trigger" onClick={()=>{setOpen("actions");setActionId(null);}} aria-expanded={open==="actions"}><span aria-hidden="true">＋</span>{text("actions")}</button>
  <button type="button" className="os-icon-button" onClick={()=>{setOpen("help");setActionId(null);}} aria-label={text("help")}>?</button>
  {open&&<Inspector title={open==="help"?text("help"):actionId==="add-user"?text("register"):action?.title[lang]??text("availableActions")} busy={taskBusy} onClose={()=>setOpen(null)} modal wide={Boolean(action)}>
   {open==="help"?<><p>{text("securityHelp")}</p><p>{c.whyText}</p><TechnicalDisclosure><p>{c.techText}</p></TechnicalDisclosure></>:!action?<div className="os-action-list">{relevant.map(item=><button key={item.id} type="button" onClick={()=>select(item.id)}><span><strong>{item.id==="add-user"?text("register"):item.title[lang]}</strong><small>{item.id==="add-user"?text("registerHelp"):item.detail[lang]}</small></span><span aria-hidden="true">↗</span></button>)}</div>:<div className="os-inline-task">
    <button type="button" disabled={taskBusy} onClick={()=>setActionId(null)}>{text("back")}</button>
    {action.id==="add-passkey"?<LivePasskeyTask lang={lang} onBusyChange={setTaskBusy}/>:action.id==="add-user"?<RegistrationsView/>:action.id==="sessions"?<SessionsView/>:action.id==="recovery"?<RecoveryView/>:<IdentityAccessWorkspace onBusyChange={setTaskBusy} initialView={action.id==="temporary-access"?"capability":"grant"}/>}
   </div>}
  </Inspector>}
 </div>;
}
